import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import * as R from 'ramda';
import useInterval from 'react-useinterval';
import sip, {
  ScopeRequestStatus,
  ScopeRequest,
  QrCodeTheme,
  QrCodeResult,
  DeepLinkResult,
} from '../services/secureIdentityProvider';
import ScopeRequestEventBuilder from './scopeRequest/events';
import { EventPayload } from '../events';
import { useFlowConfig } from './FlowConfigContext';
import { Action, Provider, SipConfig } from '../config';
import Log from '../common/logging';
import Result from '../common/result';
import { CustomError, TechnicalError } from '../common/errors';
import { Credentials } from '../common/types';
import resolver from './scopeRequest/resolver';
import { useMetadata } from './MetadataContext';
import { CredentialItem, EvidenceChannels } from '../services/secureIdentityProvider/types';
import loader, { LoadScopeRequest } from './scopeRequest/loader';
import { useAuthentication } from './AuthenticationContext';
import { useGKNService } from './GatekeeperNetworkService/GatekeeperNetworkServiceContext';
import { DocumentType } from '../services/documentCapture/documentCapture';
import { findSupportedDocumentTypes } from './scopeRequest/utils';
import { VerifiableCredential } from '../common/types/credentials';
import { getGknValue } from './GatekeeperNetworkService/utils';

export const optionalEvidences = ['idDocumentBack'];

export interface ScopeRequestProps {
  scopeRequest: ScopeRequest | null;
  loading: boolean;
  /** A flag indicating if we are waiting on the user to confirm the scope request */
  awaitingUser: boolean;
  qrCodeOrDeepLinkFetched: boolean;
  createScopeRequest(): Promise<Result<CustomError, ScopeRequest>>;
  resolveScopeRequest(response: Credentials): Promise<Result<CustomError, ScopeRequest>>;
  getEventPayload(): EventPayload;
  fetchQRCode(themeColor?: QrCodeTheme): Promise<QrCodeResult>;
  fetchDeepLink(): Promise<DeepLinkResult>;
  getScopeRequestFailureReason(scopeRequest: ScopeRequest): ScopeRequestFailure;
  getSupportedDocumentTypes(): DocumentType[];
  getRequiredCredentials(scopeRequest: ScopeRequest): string[];
  currentRequiredCredentials: () => Promise<string[]>;
  currentRequiredEvidences: string[];
  loadScopeRequestByUuid: (uuid: string) => Promise<Result<CustomError, ScopeRequest> | null>;
}

type ScopeRequestProviderProps = { children: JSX.Element };

export const ScopeRequestContext = createContext<ScopeRequestProps>({} as ScopeRequestProps);

export type ScopeRequestFailure = {
  uuid: string;
  reason?: string;
  identityResult: unknown;
};

const POLLING_DELAY = 2000;

const selectCredentials = (credentials: VerifiableCredential[], scopeRequest: ScopeRequest): VerifiableCredential[] => {
  const credentialItems = R.path(
    ['components', 'identity', 'request', 'payload', 'credentialItems'],
    scopeRequest,
  ) as CredentialItem[];
  const findCredentialItems = (identifier: string) => R.find(R.propEq('identifier', identifier), credentialItems);
  return R.filter((credential: VerifiableCredential) => !!findCredentialItems(credential.identifier), credentials);
};

/**
 * Provider and Context for ScopeRequest functions
 *
 * @param props
 * @constructor
 */
export function ScopeRequestProvider(props: ScopeRequestProviderProps): JSX.Element {
  const { gknServiceData } = useGKNService();
  const [scopeRequest, setScopeRequest] = useState<ScopeRequest | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [qrCodeOrDeepLinkFetched, setQrCodeOrDeepLinkFetched] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [awaitingUser, setAwaitingUser] = useState(true);
  const { networkName, flowInProgress, flowSuccessful, getProviderConfig, getOptionalFlowExecutionConfig, provider, credentialExpired, action } =
    useFlowConfig();
  const { metadata } = useMetadata();
  const { isLoggedIn, getUserData } = useAuthentication();
  const { children } = props;

  /**
   * This method is responsible for creating the scope request, adding it to the Context state and emitting the iframe event
   */
  async function createScopeRequest(): Promise<Result<CustomError, ScopeRequest>> {
    setLoading(true);
    // ensure current scope request is not set to a stale value
    setScopeRequest(null);
    const walletParameter = getOptionalFlowExecutionConfig('wallet');
    const redirectUrlParameter = getOptionalFlowExecutionConfig('redirectUrl');
    const appIdFromParameter = getOptionalFlowExecutionConfig('partnerAppId');
    const {
      appId,
      credentialsIssuerDid,
      scopeRequestTemplate: localConfigSrTemplateRef,
    } = getProviderConfig<SipConfig>(provider || Provider.SIP);
    setRedirectUrl(redirectUrlParameter);

    Log.debug('Getting user DID to add to scope request...');
    const userDataResult = await getUserData();
    const data = userDataResult.success();
    const { did } = data;
    Log.debug(`DID successfully retrieved: ${did}`);

    const srTemplateFromService = gknServiceData?.constraints?.public?.kyc?.scopeRequest?.requiredTemplateId;

    // For backwards compatibility with the SIP flow (QR code + v1 templates),
    // we use a locally configured template reference for SIP providers until we remove support for the SIP flow.
    // The new Gatekeeper Network service only specifies v3 templates (i.e. the web flow).
    const scopeRequestTemplate = provider === Provider.SIP ? localConfigSrTemplateRef : srTemplateFromService;
    if (!scopeRequestTemplate) {
      return Result.failed(new CustomError('Gatekeeper network has no scope request template configured'));
    }

    const creationRequest: LoadScopeRequest = {
      network: networkName,
      scopeRequestTemplate,
      appId: appIdFromParameter || appId, // This is SIP's internal partner ID, and will always be the civic-pay app when using cpass.
      cpassPartnerId: gknServiceData?.partner, // This is the externally-managed partner, i.e. the one from the GKNS which is currently the auth0 org ID.
      wallet: walletParameter || metadata.wallet,
      did,
      credentialsIssuerDid,
      timeout: getGknValue(gknServiceData!, 'scopeRequestTimeout'),
    };
    Log.debug(
      `Generating scope request with appIdFromParameter:${appIdFromParameter} appIdFromConfig:${appId} wallet: ${walletParameter} redirectUrl: ${redirectUrlParameter} scopeRequestTemplate: ${scopeRequestTemplate} did: ${did}`,
    );

    if (credentialExpired && action === Action.REFRESH) {
      loader.deleteScopeRequest(creationRequest);
    }

    const scopeRequestResult = await loader.loadOrCreateScopeRequest(creationRequest, isLoggedIn());

    setLoading(false);
    if (scopeRequestResult.isFailed()) {
      return Result.failed(scopeRequestResult.failure());
    }

    const newScopeRequest = scopeRequestResult.success();
    setScopeRequest(newScopeRequest);
    return Result.successful(newScopeRequest);
  }

  async function loadScopeRequestByUuid(uuid: string): Promise<Result<CustomError, ScopeRequest> | null> {
    setLoading(true);
    setScopeRequest(null);

    const scopeRequestResult = await loader.loadExistingScopeRequestByUuid(uuid);
    setLoading(false);
    if (scopeRequestResult.isFailed()) {
      return Result.failed(scopeRequestResult.failure());
    }

    const newScopeRequest = scopeRequestResult.success();
    setScopeRequest(newScopeRequest);
    return Result.successful(newScopeRequest);
  }

  async function resolveScopeRequest(response: Credentials): Promise<Result<CustomError, ScopeRequest>> {
    if (!scopeRequest) {
      return Result.failed(new TechnicalError('Unable to resolve scope request, scope request is null'));
    }

    // select the credentials that are requested by the scope request
    const selectedCredentials = selectCredentials(response.credentials, scopeRequest);

    const resolveResult = await resolver.resolveScopeRequest(scopeRequest, {
      credentials: selectedCredentials,
      evidences: response.evidences,
    });

    if (resolveResult.isFailed()) {
      return Result.failed(
        new TechnicalError(`Error resolving scope request: ${scopeRequest.uuid}`, resolveResult.failure()),
      );
    }

    const updatedScopeRequest = resolveResult.success();
    setScopeRequest(updatedScopeRequest);
    return Result.successful(updatedScopeRequest);
  }

  /**
   * get the scope request event payload with the scope request id
   */
  function getEventPayload(): EventPayload {
    return ScopeRequestEventBuilder.eventPayload(scopeRequest);
  }

  /**
   * manage the status for a scope request
   */
  function handleUpdatedScopeRequestStatus(status: string): void {
    switch (status) {
      case ScopeRequestStatus.AWAITING_USER:
        setAwaitingUser(true);
        break;
      case ScopeRequestStatus.USER_ACKNOWLEDGED:
        // if the status was previously awaiting user
        if (awaitingUser) {
          // emit flow in progress the first time we get user acknowledged
          flowInProgress(Provider.SIP);
        }
        setAwaitingUser(false);
        break;
      case ScopeRequestStatus.VERIFICATION_SUCCESS:
        flowSuccessful(getEventPayload());
        break;
      default:
        break;
    }
  }

  /**
   * This method is responsible for getting the scope request, and updating if the status has changed
   */
  async function fetchScopeRequestStatus(): Promise<ScopeRequest | null> {
    if (scopeRequest) {
      setUpdatingStatus(true);
      const statusResponse = await sip.getScopeRequestStatus(scopeRequest.uuid);
      statusResponse.logError('ScopeRequestProvider: Error getting scope request status');
      if (statusResponse.isSuccessful() && statusResponse.success() !== scopeRequest.status) {
        const updateScopeRequest = {
          ...scopeRequest,
          status: statusResponse.success(),
        };

        setScopeRequest(updateScopeRequest);
        handleUpdatedScopeRequestStatus(updateScopeRequest.status);
      }
      setUpdatingStatus(false);
    }
    return null;
  }

  /**
   * This method is responsible for retrieving a QR code for a scope request
   * @param themeColor - Optional theme color for QR code customisation
   */
  async function fetchQRCode(themeColor?: QrCodeTheme): Promise<QrCodeResult> {
    if (!scopeRequest) {
      return Result.failed(new TechnicalError('Cannot fetch QR code before scope request is requested'));
    }

    const { uuid: scopeRequestId } = scopeRequest as ScopeRequest;
    const qrCodeResult = await sip.retrieveQRCode(scopeRequestId, themeColor);
    setQrCodeOrDeepLinkFetched(qrCodeResult.isSuccessful());
    return qrCodeResult;
  }

  async function fetchDeepLink(): Promise<DeepLinkResult> {
    if (!scopeRequest) {
      return Result.failed(new TechnicalError('Cannot fetch deep link before scope request is requested'));
    }

    const { uuid: scopeRequestId } = scopeRequest as ScopeRequest;
    const deepLinkResult = await sip.retrieveDeepLink(scopeRequestId, redirectUrl);
    setQrCodeOrDeepLinkFetched(deepLinkResult.isSuccessful());
    return deepLinkResult;
  }

  // Get the list of docment types required by the scope request
  function getSupportedDocumentTypes(): DocumentType[] {
    return findSupportedDocumentTypes(scopeRequest);
  }

  // poll for scope request status if scope request is ready
  // and the qr code or deep link has been fetched
  // and we are not busy updating the status
  // and the scope request status is pending or acknowledged
  const shouldPollForStatus = () =>
    scopeRequest &&
    qrCodeOrDeepLinkFetched &&
    !updatingStatus &&
    R.contains(scopeRequest?.status, [ScopeRequestStatus.AWAITING_USER, ScopeRequestStatus.USER_ACKNOWLEDGED]);
  useInterval(async () => (shouldPollForStatus() ? fetchScopeRequestStatus() : null), POLLING_DELAY);

  // We accept the scopeRequest as a parameter here instead of using the one in context state,
  // because we're not guaranteed to have gone through a new render loop by the time this is called
  // after calling setScopeRequest, so the one in state might not be the latest from the PATCH call response.
  function getScopeRequestFailureReason(inScopeRequest: ScopeRequest): ScopeRequestFailure {
    if (!inScopeRequest || inScopeRequest.status !== ScopeRequestStatus.VERIFICATION_FAILED) {
      throw new Error('Unable to return scope request failure. Scope request is not in a failed state');
    }

    return {
      uuid: inScopeRequest.uuid,
      reason: inScopeRequest.reason,
      identityResult: inScopeRequest.components.identity?.result || {},
    };
  }
  const currentRequiredEvidences = useMemo((): string[] => {
    const evidences = R.path(
      ['components', 'identity', 'request', 'payload', 'channels', 'evidences'],
      scopeRequest,
    ) as EvidenceChannels;
    return R.keys(evidences);
  }, [scopeRequest]);

  const getRequiredCredentials = (inScopeRequest: ScopeRequest): string[] => {
    const credentialItems = R.path(
      ['components', 'identity', 'request', 'payload', 'credentialItems'],
      inScopeRequest,
    ) as CredentialItem[];
    const evidences = R.path(
      ['components', 'identity', 'request', 'payload', 'channels', 'evidences'],
      inScopeRequest,
    ) as EvidenceChannels;
    return [
      ...R.map(R.prop('identifier'), credentialItems),
      ...R.keys(evidences).filter((key) => !optionalEvidences.includes(key)),
    ];
  };

  const currentRequiredCredentials = useCallback((): Promise<string[]> => {
    if (!scopeRequest) {
      return Promise.resolve([]);
    }
    return Promise.resolve(getRequiredCredentials(scopeRequest));
  }, [scopeRequest]);

  return (
    <ScopeRequestContext.Provider
      value={{
        scopeRequest,
        createScopeRequest,
        resolveScopeRequest,
        getEventPayload,
        fetchQRCode,
        fetchDeepLink,
        qrCodeOrDeepLinkFetched,
        loading,
        awaitingUser,
        getScopeRequestFailureReason,
        getSupportedDocumentTypes,
        getRequiredCredentials,
        currentRequiredCredentials,
        currentRequiredEvidences,
        loadScopeRequestByUuid
      }}
    >
      {children}
    </ScopeRequestContext.Provider>
  );
}
export const useScopeRequest = (): ScopeRequestProps => useContext(ScopeRequestContext);
